<!-- eslint-disable vue/no-mutating-props -->
<script setup lang="ts">
import type { SiteBlock, SiteTeamMember } from '../../../types/site'

/**
 * Manual member list only. `source` is deliberately absent here — an app that
 * offers a live-data mode renders its own picker and delegates to this form for
 * the manual case.
 */
const props = defineProps<{ block: SiteBlock }>()

const t = useSiteEditorT()

const PLATFORMS = [
  { label: t('site.f.team.opt_website', 'Website'), value: 'website' },
  { label: t('site.f.team.opt_linkedin', 'LinkedIn'), value: 'linkedin' },
  { label: t('site.f.team.opt_facebook', 'Facebook'), value: 'facebook' },
  { label: t('site.f.team.opt_instagram', 'Instagram'), value: 'instagram' },
  { label: t('site.f.team.opt_x', 'X'), value: 'x' },
  { label: t('site.f.team.opt_youtube', 'YouTube'), value: 'youtube' },
  { label: t('site.f.team.opt_email', 'Email'), value: 'email' },
  { label: t('site.f.team.opt_phone', 'Phone'), value: 'phone' }
]

const addMember = () => {
  props.block.data.members = [...(props.block.data.members ?? []), { name: '', role: '' }]
}
const removeMember = (i: number) => {
  props.block.data.members?.splice(i, 1)
}

const addSocial = (member: SiteTeamMember) => {
  member.socials = [...(member.socials ?? []), { platform: 'website' as const, url: '' }]
}
const removeSocial = (member: SiteTeamMember, si: number) => {
  member.socials?.splice(si, 1)
}
</script>

<template>
  <!-- eslint-disable vue/no-mutating-props -->
  <UFormField :label="t('site.f.common.heading_optional', 'Heading (optional)')">
    <UInput
      v-model="block.data.headline"
      class="w-full"
    />
  </UFormField>

  <SiteEditorArrayItemCard
    v-for="(m, i) in block.data.members ?? []"
    :key="i"
    :label="`${t('site.f.team.item_label', 'Member')} ${i + 1}`"
    @remove="removeMember(i)"
  >
    <UInput
      v-model="m.name"
      :placeholder="t('site.f.team.name', 'Name')"
      class="w-full"
    />
    <UInput
      :model-value="m.role ?? ''"
      :placeholder="t('site.f.team.role', 'Role')"
      class="w-full"
      @update:model-value="m.role = String($event)"
    />
    <SiteEditorImagePicker
      :label="t('site.f.common.avatar_label', 'Avatar')"
      variant="avatar"
      :model-value="m.avatar ?? ''"
      @update:model-value="m.avatar = $event"
    />
    <UTextarea
      :model-value="m.bio ?? ''"
      :rows="2"
      :placeholder="t('site.f.team.short_bio', 'Short bio (optional)')"
      class="w-full"
      @update:model-value="m.bio = String($event)"
    />

    <div class="space-y-1.5">
      <p class="text-xs font-medium text-muted">
        {{ t('site.f.team.links_label', 'Links') }}
      </p>
      <div
        v-for="(social, si) in m.socials ?? []"
        :key="si"
        class="flex items-center gap-1.5"
      >
        <USelect
          v-model="social.platform"
          :items="PLATFORMS"
          class="w-36 shrink-0"
        />
        <UInput
          v-model="social.url"
          :placeholder="t('site.f.common.url_placeholder', 'https://...')"
          class="w-full"
        />
        <UButton
          size="xs"
          variant="ghost"
          color="error"
          icon="i-lucide-x"
          @click="removeSocial(m, si)"
        />
      </div>
      <UButton
        size="xs"
        variant="outline"
        color="neutral"
        icon="i-lucide-plus"
        @click="addSocial(m)"
      >
        {{ t('site.f.team.add_link', 'Add link') }}
      </UButton>
    </div>
  </SiteEditorArrayItemCard>

  <UButton
    size="sm"
    variant="outline"
    color="neutral"
    icon="i-lucide-plus"
    @click="addMember"
  >
    {{ t('site.f.team.add_item', 'Add member') }}
  </UButton>
</template>
